if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      connectedCallback() {
        // Initialize quantity and price tracking when connected to DOM
        if (!this.quantityPriceInitialized) {
          this.initQuantityPriceTracking();
          this.quantityPriceInitialized = true;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers",
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  "add:wait-for-subscribers",
                  startMarker,
                );
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => {
                        this.cart.renderContents(response);
                      },
                    );
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute("disabled");
          // Update button with current price when re-enabling
          if (this.updateButtonPrice) {
            this.updateButtonPrice();
          } else {
            // Fallback if updateButtonPrice not initialized yet
            this.submitButtonText.textContent =
              window.variantStrings?.addToCart || "Add to cart";
          }
        }
      }

      initQuantityPriceTracking() {
        // Find the product-info element that contains this form
        const productInfo = this.closest("product-info");
        if (!productInfo) return;

        // Find quantity input
        const quantityInput = productInfo.querySelector(".quantity__input");
        if (!quantityInput) return;

        // Listen to quantity changes
        quantityInput.addEventListener("change", () =>
          this.updateButtonPrice(),
        );
        quantityInput.addEventListener("input", () => this.updateButtonPrice());

        // Listen to variant changes to update price
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          () => {
            // Small delay to ensure price element is updated
            setTimeout(() => this.updateButtonPrice(), 100);
          },
        );

        // Initial update
        this.updateButtonPrice();
      }

      updateButtonPrice() {
        // Don't update if button is disabled or showing error state
        if (
          this.submitButton.hasAttribute("disabled") ||
          this.submitButton.getAttribute("aria-disabled") === "true"
        ) {
          return;
        }

        // Find the product-info element
        const productInfo = this.closest("product-info");
        if (!productInfo) return;

        // Get quantity
        const quantityInput = productInfo.querySelector(".quantity__input");
        if (!quantityInput) return;

        const quantity = parseInt(quantityInput.value) || 1;

        // Get price from price element
        const sectionId = this.dataset.sectionId || productInfo.dataset.section;
        let price = 0;
        let priceFormat = ""; // default currency

        // Prefer visible sale price, then regular price
        const priceContainer = document.querySelector(`#price-${sectionId}`);
        const priceElement =
          priceContainer?.querySelector(".price-item--sale [data-price]") ||
          priceContainer?.querySelector(".price__regular [data-price]");

        if (priceElement) {
          price =
            parseFloat(String(priceElement.dataset.price).replace(/,/g, "")) ||
            0;
        } else {
          // Fallback: try to get price from visible price text
          const fallbackEl =
            priceContainer?.querySelector(".price-item--sale") ||
            priceContainer?.querySelector(".price__regular .price-item--regular");
          if (fallbackEl) {
            const priceText =
              fallbackEl.textContent || fallbackEl.innerText;
            // Extract price from text like "$10.00 USD" or "$10.00"
            const priceMatch = priceText.match(/\$?([\d,]+\.?\d*)/);
            if (priceMatch) {
              price = parseFloat(priceMatch[1].replace(/,/g, ""));
              // Extract currency
              const currencyMatch = priceText.match(/([A-Z]{3})/);
              if (currencyMatch) {
                priceFormat = currencyMatch[1];
              }
            }
          }
        }

        if (!price || price <= 0) return;

        const currencySelector = document.getElementById("currency-switcher");
        priceFormat = currencySelector?.value || "USD";

        // Calculate total in selected currency
        const totalPrice = price * quantity;
        const convertedTotal =
          typeof convertPrice === "function"
            ? convertPrice(totalPrice, priceFormat)
            : totalPrice;

        this.updateButtonTextWithPrice(convertedTotal, priceFormat);
      }

      updateButtonTextWithPrice(totalPrice, currency) {
        const option = document.querySelector(
          '#currency-switcher option[value="' + currency + '"]'
        );
        const symbol = option?.dataset.symbol || "$";
        const formattedPrice =
          currency === (document.getElementById("currency-switcher")?.dataset.storeCurrency || "USD")
            ? `${symbol}${totalPrice.toFixed(2)}`
            : `${totalPrice.toFixed(2)} ${currency}`;

        // Update button text
        const addToCartText = window.variantStrings?.addToCart || "Add to cart";
        this.submitButtonText.textContent = `${addToCartText} - ${formattedPrice}`;
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}
